import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Shield, Eye, Lock, UserCheck, FileText, AlertCircle } from 'lucide-react';

const PrivacyPolicy = () => {
  const lastUpdated = "November 13, 2025";

  return (
    <div className="min-h-screen bg-[#F7F6F2]">
      <Helmet>
        <title>Privacy Policy | KGOB CPAs & Advisors</title>
        <meta name="description" content="Privacy Policy for Kohari Gonzalez Oneyear & Brown CPAs & Advisors. Learn how we protect your personal information." />
        <link rel="canonical" href="https://kgob.com/privacy-policy" />
      </Helmet>
      <Header />
      
      <main className="py-16 lg:py-20">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#D4AF37]/10 mb-6">
              <Shield className="w-8 h-8 text-[#D4AF37]" />
            </div>
            <h1 className="font-['Playfair_Display'] text-4xl lg:text-5xl font-bold text-[#0B2240] mb-4">
              Privacy Policy
            </h1>
            <p className="text-lg text-[#4C5A70]">
              Last Updated: {lastUpdated}
            </p>
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="prose prose-lg max-w-none"
          >
            <div className="bg-white rounded-2xl p-8 lg:p-12 shadow-[0_8px_30px_rgba(11,34,64,0.12)]">
              
              {/* Introduction */}
              <section className="mb-10">
                <h2 className="font-['Playfair_Display'] text-2xl font-semibold text-[#0B2240] mb-4 flex items-center gap-3">
                  <Eye className="w-6 h-6 text-[#D4AF37]" />
                  Introduction
                </h2>
                <p className="text-[#4C5A70] leading-relaxed mb-4">
                  Kohari Gonzalez Oneyear & Brown CPAs & Advisors ("KGOB," "we," "our," or "us") is committed to protecting your privacy and maintaining the confidentiality of your personal and financial information. As a professional accounting firm operating in North Carolina, we are bound by strict confidentiality requirements under the American Institute of CPAs (AICPA) Code of Professional Conduct and North Carolina General Statutes.
                </p>
                <p className="text-[#4C5A70] leading-relaxed">
                  This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website <a data-testid="privacy-policy-a-4" href="https://kgob.com" className="text-[#D4AF37] hover:underline">kgob.com</a>, use our services, or engage with us through email marketing campaigns.
                </p>
              </section>

              {/* Information We Collect */}
              <section className="mb-10">
                <h2 className="font-['Playfair_Display'] text-2xl font-semibold text-[#0B2240] mb-4 flex items-center gap-3">
                  <FileText className="w-6 h-6 text-[#D4AF37]" />
                  Information We Collect
                </h2>
                
                <h3 className="text-xl font-semibold text-[#0B2240] mt-6 mb-3">Personal Information You Provide</h3>
                <p className="text-[#4C5A70] mb-3">When you interact with our services, we may collect:</p>
                <ul className="list-disc list-inside text-[#4C5A70] space-y-2 mb-4">
                  <li><strong>Contact Information:</strong> Name, title/salutation, email address, phone number, mailing address</li>
                  <li><strong>Business Information:</strong> Company name, business type, industry</li>
                  <li><strong>Financial Information:</strong> Tax documents, financial statements (only when you engage our professional services)</li>
                  <li><strong>Marketing Preferences:</strong> Interest type (business owner or individual/family), preferred office location, consent to receive marketing communications</li>
                  <li><strong>Consultation Booking:</strong> Preferred date/time, consultation type, questions or concerns</li>
                </ul>

                <h3 className="text-xl font-semibold text-[#0B2240] mt-6 mb-3">Automatically Collected Information</h3>
                <ul className="list-disc list-inside text-[#4C5A70] space-y-2">
                  <li><strong>Usage Data:</strong> Pages visited, time spent on site, referring URLs</li>
                  <li><strong>Device Information:</strong> Browser type, operating system, IP address</li>
                  <li><strong>Cookies:</strong> Session cookies for website functionality and analytics</li>
                </ul>
              </section>

              {/* How We Use Your Information */}
              <section className="mb-10">
                <h2 className="font-['Playfair_Display'] text-2xl font-semibold text-[#0B2240] mb-4 flex items-center gap-3">
                  <UserCheck className="w-6 h-6 text-[#D4AF37]" />
                  How We Use Your Information
                </h2>
                <p className="text-[#4C5A70] mb-3">We use your information for the following purposes:</p>
                <ul className="list-disc list-inside text-[#4C5A70] space-y-2">
                  <li><strong>Professional Services:</strong> To provide accounting, tax preparation, advisory, and consulting services</li>
                  <li><strong>Communication:</strong> To respond to inquiries, schedule consultations, and provide service updates</li>
                  <li><strong>Marketing:</strong> To send newsletters, tax tips, event invitations, and promotional offers (only with your explicit consent)</li>
                  <li><strong>Event Management:</strong> To manage RSVPs for community events (Santa + Toys for Tots, Free Coffee Fridays, Networking Breakfasts)</li>
                  <li><strong>Website Improvement:</strong> To analyze usage patterns and enhance user experience</li>
                  <li><strong>Legal Compliance:</strong> To comply with applicable laws, regulations, and professional standards</li>
                </ul>
              </section>

              {/* Professional Confidentiality */}
              <section className="mb-10">
                <h2 className="font-['Playfair_Display'] text-2xl font-semibold text-[#0B2240] mb-4 flex items-center gap-3">
                  <Lock className="w-6 h-6 text-[#D4AF37]" />
                  Professional Confidentiality (AICPA & NC Law)
                </h2>
                <div className="bg-[#FFF9E6] border-l-4 border-[#D4AF37] p-6 rounded-lg mb-4">
                  <p className="text-[#0B2240] font-semibold mb-2">
                    CPA-Client Privilege
                  </p>
                  <p className="text-[#4C5A70]">
                    Under North Carolina law (21 NCAC 08N .0205) and the AICPA Code of Professional Conduct, we have a strict duty of confidentiality. We will not disclose any confidential client information obtained during the course of providing professional services without your express consent, except as required or permitted by law.
                  </p>
                </div>
                <p className="text-[#4C5A70]">
                  This means your tax returns, financial statements, business strategies, and personal financial information are protected with the same level of confidentiality as attorney-client privilege in North Carolina.
                </p>
              </section>

              {/* Information Sharing */}
              <section className="mb-10">
                <h2 className="font-['Playfair_Display'] text-2xl font-semibold text-[#0B2240] mb-4">
                  Information Sharing and Disclosure
                </h2>
                <p className="text-[#4C5A70] mb-3">We may share your information only in the following limited circumstances:</p>
                <ul className="list-disc list-inside text-[#4C5A70] space-y-2">
                  <li><strong>With Your Consent:</strong> When you explicitly authorize us to share information</li>
                  <li><strong>Service Providers:</strong> With third-party service providers who assist us (e.g., software platforms, email service providers) under strict confidentiality agreements</li>
                  <li><strong>Professional Advisors:</strong> With attorneys, consultants, or other professionals assisting with your engagement</li>
                  <li><strong>Legal Requirements:</strong> When required by law, court order, subpoena, or government regulation</li>
                  <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets (with notice to you)</li>
                </ul>
                <p className="text-[#4C5A70] mt-4 font-semibold">
                  We do NOT sell your personal information to third parties.
                </p>
              </section>

              {/* Your Rights (CCPA/GDPR) */}
              <section className="mb-10">
                <h2 className="font-['Playfair_Display'] text-2xl font-semibold text-[#0B2240] mb-4 flex items-center gap-3">
                  <AlertCircle className="w-6 h-6 text-[#D4AF37]" />
                  Your Privacy Rights
                </h2>
                <p className="text-[#4C5A70] mb-3">Under the California Consumer Privacy Act (CCPA), General Data Protection Regulation (GDPR), and North Carolina law, you have the following rights:</p>
                
                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-[#0B2240] mb-1">Right to Access</h4>
                    <p className="text-[#4C5A70] text-sm">Request a copy of the personal information we hold about you</p>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-[#0B2240] mb-1">Right to Correction</h4>
                    <p className="text-[#4C5A70] text-sm">Request correction of inaccurate or incomplete information</p>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-[#0B2240] mb-1">Right to Deletion</h4>
                    <p className="text-[#4C5A70] text-sm">Request deletion of your personal information (subject to legal retention requirements)</p>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-[#0B2240] mb-1">Right to Opt-Out</h4>
                    <p className="text-[#4C5A70] text-sm">Opt out of marketing communications at any time (click "Unsubscribe" in any email)</p>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-[#0B2240] mb-1">Right to Data Portability</h4>
                    <p className="text-[#4C5A70] text-sm">Request your data in a portable format</p>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-[#0B2240] mb-1">Right to Non-Discrimination</h4>
                    <p className="text-[#4C5A70] text-sm">We will not discriminate against you for exercising your privacy rights</p>
                  </div>
                </div>

                <p className="text-[#4C5A70] mt-4">
                  To exercise any of these rights, contact us at <a data-testid="privacy-policy-a-1" href="mailto:privacy@kgob.com" className="text-[#D4AF37] hover:underline">privacy@kgob.com</a> or call <a data-testid="privacy-policy-a-3" href="tel:7045993355" className="text-[#D4AF37] hover:underline">(704) 599-3355</a>. We will respond within 45 days.
                </p>
              </section>

              {/* Email Marketing */}
              <section className="mb-10">
                <h2 className="font-['Playfair_Display'] text-2xl font-semibold text-[#0B2240] mb-4">
                  Email Marketing & Consent
                </h2>
                <p className="text-[#4C5A70] mb-3">
                  When you sign up for our Holiday Money-Saver Kit or other resources, you may opt in to receive:
                </p>
                <ul className="list-disc list-inside text-[#4C5A70] space-y-2 mb-4">
                  <li>Tax tips and year-end planning guides</li>
                  <li>Invitations to community events (Santa + Toys for Tots, Free Coffee Fridays, Networking Breakfasts)</li>
                  <li>Updates about our services and special offers</li>
                </ul>
                <p className="text-[#4C5A70] mb-3">
                  <strong>Your consent is explicit and voluntary.</strong> We only send marketing emails to those who have checked the consent box. You can unsubscribe at any time by clicking the "Unsubscribe" link in any email or contacting us directly.
                </p>
              </section>

              {/* Data Security */}
              <section className="mb-10">
                <h2 className="font-['Playfair_Display'] text-2xl font-semibold text-[#0B2240] mb-4">
                  Data Security
                </h2>
                <p className="text-[#4C5A70] mb-3">
                  We implement industry-standard security measures to protect your information:
                </p>
                <ul className="list-disc list-inside text-[#4C5A70] space-y-2">
                  <li>Encryption of data in transit and at rest (TLS/SSL)</li>
                  <li>Secure cloud storage with MongoDB Atlas (SOC 2 Type II certified)</li>
                  <li>Access controls and authentication (JWT tokens, password hashing)</li>
                  <li>Regular security audits and monitoring via Sentry</li>
                  <li>Employee training on data protection and confidentiality</li>
                </ul>
              </section>

              {/* Data Retention */}
              <section className="mb-10">
                <h2 className="font-['Playfair_Display'] text-2xl font-semibold text-[#0B2240] mb-4">
                  Data Retention
                </h2>
                <p className="text-[#4C5A70] mb-3">
                  We retain your information for different periods depending on its purpose:
                </p>
                <ul className="list-disc list-inside text-[#4C5A70] space-y-2">
                  <li><strong>Client Records:</strong> 7 years after engagement completion (IRS requirement for tax records)</li>
                  <li><strong>Marketing Leads:</strong> Until you request deletion or unsubscribe (minimum 2 years for compliance proof)</li>
                  <li><strong>Consultation Records:</strong> 3 years after last contact</li>
                  <li><strong>Event RSVPs:</strong> 1 year after event date</li>
                </ul>
              </section>

              {/* Third-Party Services */}
              <section className="mb-10">
                <h2 className="font-['Playfair_Display'] text-2xl font-semibold text-[#0B2240] mb-4">
                  Third-Party Services
                </h2>
                <p className="text-[#4C5A70] mb-3">
                  We use the following third-party services to operate our business:
                </p>
                <ul className="list-disc list-inside text-[#4C5A70] space-y-2">
                  <li><strong>Email Service:</strong> Resend (for transactional and marketing emails)</li>
                  <li><strong>Scheduling:</strong> Custom In-House System (for consultation booking)</li>
                  <li><strong>Video Conferencing:</strong> Zoom (for virtual meetings)</li>
                  <li><strong>Analytics:</strong> Sentry (for error tracking and performance monitoring)</li>
                  <li><strong>Database:</strong> MongoDB Atlas (for secure data storage)</li>
                </ul>
                <p className="text-[#4C5A70] mt-3">
                  Each service provider has their own privacy policy and is required to maintain confidentiality under our service agreements.
                </p>
              </section>

              {/* Cookies */}
              <section className="mb-10">
                <h2 className="font-['Playfair_Display'] text-2xl font-semibold text-[#0B2240] mb-4">
                  Cookies and Tracking
                </h2>
                <p className="text-[#4C5A70] mb-3">
                  We use cookies and similar tracking technologies to enhance your experience:
                </p>
                <ul className="list-disc list-inside text-[#4C5A70] space-y-2">
                  <li><strong>Essential Cookies:</strong> Required for website functionality (e.g., session management)</li>
                  <li><strong>Analytics Cookies:</strong> Help us understand how visitors use our site</li>
                  <li><strong>Preference Cookies:</strong> Remember your settings and preferences</li>
                </ul>
                <p className="text-[#4C5A70] mt-3">
                  You can control cookies through your browser settings. Disabling cookies may limit website functionality.
                </p>
              </section>

              {/* Children's Privacy */}
              <section className="mb-10">
                <h2 className="font-['Playfair_Display'] text-2xl font-semibold text-[#0B2240] mb-4">
                  Children's Privacy
                </h2>
                <p className="text-[#4C5A70]">
                  Our services are not directed to individuals under 18. We do not knowingly collect personal information from children. If you believe we have inadvertently collected information from a minor, please contact us immediately.
                </p>
              </section>

              {/* Changes to This Policy */}
              <section className="mb-10">
                <h2 className="font-['Playfair_Display'] text-2xl font-semibold text-[#0B2240] mb-4">
                  Changes to This Privacy Policy
                </h2>
                <p className="text-[#4C5A70]">
                  We may update this Privacy Policy annually or as required by law. We will notify you of material changes by email (if you've provided one) or by posting a notice on our website. Your continued use of our services after changes constitutes acceptance of the updated policy.
                </p>
              </section>

              {/* Contact */}
              <section>
                <h2 className="font-['Playfair_Display'] text-2xl font-semibold text-[#0B2240] mb-4">
                  Contact Us
                </h2>
                <p className="text-[#4C5A70] mb-4">
                  If you have questions about this Privacy Policy or wish to exercise your privacy rights, please contact:
                </p>
                <div className="bg-[#F7F6F2] p-6 rounded-lg">
                  <p className="text-[#0B2240] font-semibold mb-2">Kohari Gonzalez Oneyear & Brown CPAs & Advisors</p>
                  <p className="text-[#4C5A70] text-sm">
                    <strong>Privacy Officer</strong><br />
                    2740 East WT Harris Blvd, Suite 200<br />
                    Charlotte, NC 28213<br />
                    Phone: <a data-testid="privacy-policy-a-2" href="tel:7045993355" className="text-[#D4AF37] hover:underline">(704) 599-3355</a><br />
                    Email: <a data-testid="privacy-policy-a-1" href="mailto:privacy@kgob.com" className="text-[#D4AF37] hover:underline">privacy@kgob.com</a>
                  </p>
                </div>
              </section>

            </div>
          </motion.div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
